import { useState } from 'react';
import { useT } from '../../i18n/i18n';
import { removeInspectorPhotoAction, setInspectorPhotoAction, useInspectorPhotoUrl, type InspectorPhotoLane } from '../../state/inspectorPhoto';
import { useInspectorProfile } from '../../state/preferences';
import { Button, ConfirmSheet, Icon, showToast } from '../../ui/components';
import { initialsOf } from '../inspections/inspectionSummary';

// The inspector's avatar (Owner 2026-09-14): the inspector's own photo when one was set, otherwise the
// initials of the profile name, otherwise the person glyph. `bar` is the top-bar size (44px with a
// photo — the face must be recognisable), `large` the profile-page size (96px).
export function InspectorAvatar(props: { size?: 'bar' | 'large'; testId?: string }) {
  const profile = useInspectorProfile();
  const url = useInspectorPhotoUrl(props.size === 'large' ? 'full' : 'thumbnail');
  const name = profile.name.trim();
  const classes = ['avatar', props.size === 'large' ? 'avatar-large' : '', url ? 'avatar-photo' : ''].filter((c) => c.length > 0).join(' ');
  return (
    <span className={classes} data-testid={props.testId ?? 'inspector-avatar'} data-photo={url ? 'true' : 'false'}>
      {url ? <img src={url} alt="" /> : name.length > 0 ? <span className="avatar-initials">{initialsOf(name)}</span> : <Icon name="person" />}
    </span>
  );
}

// Settings › 验屋师资料: the photo block — the large avatar with Take Photo / Choose Library and, once a
// photo exists, Remove (asks first). Removing only removes the app's copy (no album API exists here).
export function InspectorPhotoBlock() {
  const { t } = useT();
  const profile = useInspectorProfile();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const hasPhoto = profile.photoUpdatedAt !== null;

  async function pick(lane: InspectorPhotoLane) {
    setBusy(true);
    try {
      const outcome = await setInspectorPhotoAction(lane);
      if (outcome.kind === 'error') showToast(t('profile.photoFailed', { message: outcome.message }), 'info');
      else if (outcome.kind === 'saved' && outcome.album && outcome.album.state === 'failed') showToast(outcome.album.detail, 'info');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setConfirm(false);
    setBusy(true);
    try {
      await removeInspectorPhotoAction();
    } catch (raw) {
      showToast(t('profile.photoFailed', { message: raw instanceof Error ? raw.message : String(raw) }), 'info');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="profile-photo" data-testid="profile-photo" data-has-photo={hasPhoto ? 'true' : 'false'}>
      <InspectorAvatar size="large" testId="profile-avatar" />
      <div className="profile-photo-actions">
        <Button variant="secondary" size="small" block={false} icon="photo_camera" onClick={() => void pick('camera')} disabled={busy} testId="profile-photo-camera">
          {t('photo.takePhoto')}
        </Button>
        <Button variant="secondary" size="small" block={false} icon="photo_library" onClick={() => void pick('library')} disabled={busy} testId="profile-photo-library">
          {t('photo.chooseLibrary')}
        </Button>
        {hasPhoto ? (
          <Button variant="text" size="small" block={false} onClick={() => setConfirm(true)} disabled={busy} testId="profile-photo-remove">
            {t('profile.photoRemove')}
          </Button>
        ) : null}
      </div>
      <p className="card-note profile-photo-note">{t('profile.photoBody')}</p>
      {confirm ? (
        <ConfirmSheet title={t('profile.photoRemoveTitle')} body={t('profile.photoRemoveBody')} confirmLabel={t('profile.photoRemove')} danger onConfirm={() => void remove()} onCancel={() => setConfirm(false)} testId="profile-photo-confirm" />
      ) : null}
    </div>
  );
}
